import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LOGO_URL } from '../lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Handles the return leg of Emergent Google Auth.
 * URL contains #session_id=<id> — exchange it via our backend, then either
 * send the user to their dashboard or to /auth/pick-role if they're brand new.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const roleDashboard = (role) => (role === 'admin' ? '/admin' : role === 'organizer' ? '/organizer' : '/player-dashboard');

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const match = location.hash && location.hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    // Clear the hash so a refresh doesn't re-run the exchange.
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        if (data.needs_role) {
          navigate('/auth/pick-role', {
            replace: true,
            state: { temp_token: data.temp_token, email: data.email, name: data.name, picture: data.picture },
          });
          return;
        }
        // Cookies are set — re-sync auth context, then send to dashboard.
        await checkAuth();
        navigate(roleDashboard(data.user?.role), { replace: true });
      } catch (e) {
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Google sign-in failed. Please try again.');
      }
    })();
  }, [location.hash, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 court-pattern">
      <div className="max-w-md w-full text-center animate-fade-up">
        <img src={LOGO_URL} alt="Beyond Ball India" className="h-14 w-14 rounded-full object-contain mx-auto mb-6" />
        {error ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="auth-callback-error-title">Sign-in failed</h1>
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-6" data-testid="auth-callback-error">
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="btn-primary px-5 py-3 rounded-xl text-sm font-semibold"
              data-testid="auth-callback-back-btn"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="auth-callback-title">Signing you in…</h1>
            <p className="text-sm text-gray-600">One moment while we finish setting up your session.</p>
            <div className="mt-6 h-1.5 w-32 mx-auto rounded-full bg-orange-100 overflow-hidden">
              <div className="h-full bg-[#F97316] animate-pulse w-full" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
